/** What an import key is allowed to write, at a glance.
 *
 *  An import key decides what an import may create and update, and a missing
 *  permission does not fail the run — the importer drops the entity and logs at
 *  debug. So a sync on the wrong key finishes, reports success, and imports
 *  nothing. That is why the permission belongs in the picker rather than three
 *  clicks away in the key itself.
 *
 *  Six flags matter to a product sync (create/update × tours, activities,
 *  itineraries), and six checkboxes would be unreadable in a dropdown row. Each
 *  kind is therefore one chip with a two-slot state — `CU` both, `C·` create
 *  only, `··` neither — which fits on one line and still says exactly which
 *  half is missing.
 *
 *  The key's other six flags (users, staff users, tour users) are deliberately
 *  not shown: a product sync never writes them, so here they are noise.
 */
import { Stack, Tooltip, Typography } from '@mui/material';

import type { ImportSourceFlags } from '@/api/integrations';

/** The catalogue kinds, in the order the backend lists them. */
const KINDS: { key: keyof ImportSourceFlags; label: string; short: string }[] = [
  { key: 'tours', label: 'tours', short: 'Tours' },
  { key: 'activities', label: 'activities', short: 'Activities' },
  { key: 'itineraries', label: 'itineraries', short: 'Itineraries' },
];

interface Props {
  flags: ImportSourceFlags | undefined;
  /** Kinds this run will write. Those are the only ones whose absence is a
   *  problem, so a tours-only run does not flag missing activity permissions. */
  required?: string[];
  /** Tighter type for dropdown rows. */
  dense?: boolean;
}

export function ImportKeyFlags({ flags, required, dense = false }: Props) {
  if (!flags) return null;
  const needed = new Set(required ?? []);

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {KINDS.map(({ key, label, short }) => {
        const state = flags[key];
        if (!state) return null;
        const isRequired = needed.has(key);
        const lacking = isRequired && (!state.create || !state.update);
        const title = lacking
          ? `This run writes ${label}, but the key cannot ${[
              !state.create && 'create',
              !state.update && 'update',
            ]
              .filter(Boolean)
              .join(' or ')} them — they would be skipped silently.`
          : `${state.create ? 'Can' : 'Cannot'} create, ${
              state.update ? 'can' : 'cannot'
            } update ${label}.`;

        return (
          <Tooltip key={key} title={title}>
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                px: 0.5,
                borderRadius: 0.5,
                border: 1,
                borderColor: lacking ? 'error.main' : 'divider',
                color: lacking ? 'error.main' : 'text.secondary',
                fontWeight: isRequired ? 600 : 400,
                whiteSpace: 'nowrap',
                fontSize: dense ? 10 : 11,
              }}
            >
              {short} {state.create ? 'C' : '·'}
              {state.update ? 'U' : '·'}
            </Typography>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

/** Which catalogue kinds a key cannot fully write, given what the run needs. */
export function lackingKinds(flags: ImportSourceFlags | undefined, required: string[]): string[] {
  if (!flags) return required;
  return required.filter((kind) => {
    const state = flags[kind as keyof ImportSourceFlags];
    return !state || !state.create || !state.update;
  });
}

/** The key a run should use when the operator has not chosen one.
 *
 *  Picking a key by hand should be for when you want a SPECIFIC one, not a step
 *  everyone pays on every run. So the current value is kept when it can write
 *  the selected kinds, and otherwise the first enabled key that can is chosen.
 *
 *  When nothing matches, `current` is returned unchanged rather than null: the
 *  caller's gate can then name what is missing and offer to mint a key, which
 *  is more use than an empty field.
 */
export function autoSelectKey<
  T extends { code: string; is_enabled: boolean; allow_import: ImportSourceFlags },
>(keys: T[], required: string[], current: string | null): string | null {
  const fits = (k: T) => lackingKinds(k.allow_import, required).length === 0;
  const chosen = keys.find((k) => k.code === current);
  if (chosen && fits(chosen)) return current;
  return keys.find((k) => k.is_enabled && fits(k))?.code ?? current;
}
